/**
 * VoucherModal — create/edit a voucher (balanced Dr/Cr lines).
 * @file src/web/features/admin/components/VoucherModal.tsx
 *
 * Manual vouchers are created via POST /api/books/vouchers; editing an
 * existing (incl. Tally) voucher PATCHes it and marks it edited (protected
 * from re-import). Debits must equal credits to save.
 */

import { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { LedgerPicker, type LedgerOption } from './LedgerPicker';
import { VOUCHER_TYPES, DEFAULT_VOUCHER_TYPE } from '../../../shared/utils/voucher-types';

interface Line { ledgerName: string; amount: string; isDebit: boolean }
interface Props { entityId: string | null; voucherId: string | null; initialType?: string; onClose: () => void; onSaved: () => void }

const inputCls = 'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';
const num = (s: string) => (Number(s) || 0);

export function VoucherModal({ entityId, voucherId, initialType, onClose, onSaved }: Props) {
  const editing = !!voucherId;
  const [names, setNames] = useState<LedgerOption[]>([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), type: initialType || DEFAULT_VOUCHER_TYPE, number: '', narration: '', supplierInvoiceNo: '', supplierInvoiceDate: '' });
  const [lines, setLines] = useState<Line[]>([{ ledgerName: '', amount: '', isDebit: true }, { ledgerName: '', amount: '', isDebit: false }]);
  const [manual, setManual] = useState(true);
  // The type the voucher was opened with — and whether Tally is where it came
  // from — so a non-standard one stays selectable after trying another.
  const [openedType, setOpenedType] = useState<string | null>(null);
  const [openedFromTally, setOpenedFromTally] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNames();
    if (voucherId) {
      adminFetchJson<any>(`/api/tally-import/vouchers/${voucherId}`, { query: { entityId: entityId ?? undefined } })
        .then((d) => {
          setOpenedType(d.voucher.type);
          setOpenedFromTally(d.voucher.source === 'tally');
          setForm({ date: d.voucher.date, type: d.voucher.type, number: d.voucher.number ?? '', narration: d.voucher.narration ?? '', supplierInvoiceNo: d.voucher.supplierInvoiceNo ?? '', supplierInvoiceDate: d.voucher.supplierInvoiceDate ?? '' });
          setLines(d.entries.map((e: any) => ({ ledgerName: e.ledgerName, amount: String(e.amount), isDebit: e.isDebit })));
          setManual(d.voucher.source === 'manual');
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [voucherId, entityId]);

  const loadNames = () =>
    adminFetchJson<{ items: LedgerOption[] }>('/api/tally-import/ledger-names', { query: { entityId: entityId ?? undefined } })
      .then((d) => setNames(d.items)).catch(() => {});

  const setLine = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, x) => (x === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { ledgerName: '', amount: '', isDebit: false }]);
  const rmLine = (i: number) => setLines((ls) => (ls.length > 2 ? ls.filter((_, x) => x !== i) : ls));

  const totalDr = lines.reduce((s, l) => s + (l.isDebit ? num(l.amount) : 0), 0);
  const totalCr = lines.reduce((s, l) => s + (!l.isDebit ? num(l.amount) : 0), 0);
  const diff = Math.round((totalDr - totalCr) * 100) / 100;
  const balanced = Math.abs(diff) < 0.01 && totalDr > 0;

  /* A bill from a supplier carries THEIR invoice number, which is what they file
     and what GSTR-2B lists. Our voucher number is an internal sequence and
     never matches it — so on the voucher types a purchase bill is booked
     through, ask for the supplier's number separately. */
  const takesSupplierInvoice = /^(purchase|debit note|journal)$/i.test(form.type.trim());

  const save = async () => {
    setError(null);
    if (!balanced) { setError(diff === 0 ? 'Enter amounts on both sides' : `Out of balance by ₹${Math.abs(diff).toFixed(2)}`); return; }
    setSaving(true);
    try {
      const { supplierInvoiceNo, supplierInvoiceDate, ...head } = form;
      const body = {
        ...head, entityId,
        // Only sent where the field is shown, so switching a voucher to Sales
        // does not silently clear a number captured earlier.
        ...(takesSupplierInvoice ? { supplierInvoiceNo: supplierInvoiceNo.trim() || null, supplierInvoiceDate: supplierInvoiceDate || null } : {}),
        entries: lines.filter((l) => l.ledgerName.trim() && num(l.amount) > 0).map((l) => ({ ledgerName: l.ledgerName.trim(), amount: num(l.amount), isDebit: l.isDebit })),
      };
      if (editing) await adminFetchJson(`/api/books/vouchers/${voucherId}`, { method: 'PATCH', body: JSON.stringify(body) });
      else await adminFetchJson('/api/books/vouchers', { method: 'POST', body: JSON.stringify(body) });
      onSaved();
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Could not save'); setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`/api/books/vouchers/${voucherId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not delete');
      onSaved();
    } catch (e: any) { setError(e.message || 'Could not delete'); setSaving(false); setConfirming(false); setTyped(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl border border-ink-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <h3 className="text-lg font-semibold text-ink-900">{editing ? 'Edit voucher' : 'New voucher'}</h3>
            {/* Where this voucher came from changes what editing and deleting
                mean, so it is stated at the top rather than left to be inferred
                from a warning further down. */}
            {editing && (
              manual
                ? <span className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-ink-50 px-2.5 py-0.5 text-[11px] font-semibold text-ink-600">Created here</span>
                : <span className="inline-flex items-center gap-1 rounded-full border border-warn-200 bg-warn-50 px-2.5 py-0.5 text-[11px] font-semibold text-warn-700">From Tally</span>
            )}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="flex min-h-[20vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink-300" /></div>
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
            {editing && !manual && <div className="rounded-xl border border-warn-200 bg-warn-50 px-3.5 py-2.5 text-xs text-warn-700">This voucher came from the client's Tally. Saving marks it edited so a re-import will not overwrite your change, and queues the change for their Tally.</div>}
            {editing && manual && <div className="rounded-xl border border-ink-200 bg-ink-50 px-3.5 py-2.5 text-xs text-ink-600">Created here, not imported. It is queued to be added to the client's Tally once they confirm; until then it exists only in oddits.</div>}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></Field>
              <Field label="Type">
                {/* A list to pick from, not a box to type in. It was an input
                    with suggestions, prefilled "Journal" — so the browser
                    offered only the suggestion matching what was already
                    there, and the other seven types looked missing. It also
                    took any text at all. */}
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                  {VOUCHER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  {/* A type outside the list is shown and kept, never offered
                      anew. From Tally it is one the client defined there, and
                      is theirs. Anywhere else it was typed into the old
                      free-text box, and wants correcting. */}
                  {openedType && !(VOUCHER_TYPES as readonly string[]).includes(openedType) && (
                    <option value={openedType}>{openedType} {openedFromTally ? '(from Tally)' : '(old type — please change)'}</option>
                  )}
                </select>
              </Field>
              <Field label="Number"><input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputCls} placeholder="Auto from series, if set" title="Leave blank and a new voucher takes the next number of the document series defined for its type. A number typed here is kept as typed." /></Field>
              <div className="col-span-2 sm:col-span-1"><Field label="Narration"><input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className={inputCls} placeholder="Optional" /></Field></div>
              {takesSupplierInvoice && (
                <>
                  <div className="col-span-2"><Field label="Supplier invoice no."><input value={form.supplierInvoiceNo} onChange={(e) => setForm({ ...form, supplierInvoiceNo: e.target.value })} className={inputCls} placeholder="As printed on the supplier's bill — GSTR-2B matches on this" /></Field></div>
                  <Field label="Supplier invoice date"><input type="date" value={form.supplierInvoiceDate} onChange={(e) => setForm({ ...form, supplierInvoiceDate: e.target.value })} className={inputCls} /></Field>
                </>
              )}
            </div>

            <div className="overflow-hidden rounded-xl border border-ink-200">
              <table className="w-full text-sm">
                <thead><tr className="bg-ink-50 text-xs uppercase tracking-wide text-ink-400">
                  <th className="px-3 py-2 text-left font-medium">Ledger</th>
                  <th className="w-20 px-3 py-2 text-center font-medium">Dr/Cr</th>
                  <th className="w-32 px-3 py-2 text-right font-medium">Amount</th>
                  <th className="w-10" />
                </tr></thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-t border-ink-100">
                      <td className="px-3 py-1.5">
                        <LedgerPicker
                          value={l.ledgerName}
                          options={names}
                          entityId={entityId}
                          onChange={(n) => setLine(i, { ledgerName: n })}
                          onCreated={(n) => { loadNames().then(() => setLine(i, { ledgerName: n })); }}
                          className={inputCls}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <button type="button" onClick={() => setLine(i, { isDebit: !l.isDebit })} className={`w-full rounded-lg px-2 py-2 text-xs font-semibold ${l.isDebit ? 'bg-info-50 text-info-700' : 'bg-highlighter-40 text-inkblack'}`}>{l.isDebit ? 'Dr' : 'Cr'}</button>
                      </td>
                      <td className="px-3 py-1.5"><input type="number" step="0.01" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} className={`${inputCls} text-right`} placeholder="0.00" /></td>
                      <td className="px-1 py-1.5 text-center"><button type="button" onClick={() => rmLine(i)} className="text-ink-300 hover:text-danger-500"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50 text-sm font-semibold">
                    <td className="px-3 py-2"><button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-semibold text-inkblack"><Plus className="h-3.5 w-3.5" /> Add line</button></td>
                    <td className="px-3 py-2 text-right text-xs text-ink-500">Dr {totalDr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right text-xs text-ink-500">Cr {totalCr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="text-right text-xs">
              {balanced ? <span className="font-medium text-ok-600">Balanced ✓</span> : <span className="font-medium text-warn-600">{diff === 0 ? 'Enter amounts' : `Out of balance by ₹${Math.abs(diff).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}</span>}
            </div>
          </div>
        )}

        {confirming && (
          <div className="shrink-0 border-t border-danger-200 bg-danger-50 px-6 py-4">
            <p className="flex items-start gap-2 text-sm font-semibold text-danger-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Delete this {form.type.toLowerCase()}{form.number ? ` #${form.number}` : ''}?
            </p>
            {/* The two cases genuinely differ, so the copy does too rather than
                saying something vague enough to cover both. */}
            <ul className="mt-2 space-y-1 pl-6 text-xs text-danger-700">
              <li>Both sides go — a voucher is the double entry, you cannot remove one leg.</li>
              {manual ? (
                <li>This one was created here and never sent to Tally, so it is removed outright.</li>
              ) : (
                <>
                  <li>It came from Tally, so it is hidden here and a deletion is <strong>queued</strong> for the client's Tally.</li>
                  <li>Nothing changes in their books until they confirm it in the desktop app — and if they decline, it comes back.</li>
                </>
              )}
            </ul>
            <p className="mt-3 text-xs text-danger-700">Type <span className="font-semibold">yes</span> to confirm.</p>
            <div className="mt-2 flex items-center gap-2">
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="yes"
                autoFocus
                className="w-28 rounded-lg border border-danger-300 bg-white px-3 py-1.5 text-sm focus:border-danger-500 focus:outline-none"
              />
              <button
                onClick={del}
                disabled={typed.trim().toLowerCase() !== 'yes' || saving}
                className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {manual ? 'Delete voucher' : 'Delete and queue for Tally'}
              </button>
              <button onClick={() => { setConfirming(false); setTyped(''); }} className="rounded-full px-3 py-1.5 text-sm font-medium text-ink-600 hover:text-ink-800">Keep it</button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 border-t border-ink-100 px-6 py-4">
          {editing && !confirming && <button onClick={() => setConfirming(true)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full border border-danger-200 bg-white px-4 py-2 text-sm font-semibold text-danger-600 hover:bg-danger-50"><Trash2 className="h-4 w-4" /> Delete</button>}
          <button onClick={onClose} className="ml-auto rounded-full border border-ink-300 bg-white px-5 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button onClick={save} disabled={saving || loading} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? 'Save changes' : 'Create voucher'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><label className="text-xs font-medium text-ink-500">{label}</label>{children}</div>;
}
